import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, Chip, Button, FormControl, InputLabel, Select, MenuItem,
    Collapse, Tooltip, CircularProgress, TablePagination, Menu, ListItemIcon, ListItemText,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';

import api from '../../services/api';
import BuscaPaciente from '../BuscaPaciente';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, alertValidacao } from '../../services/alerts';
import {
    STATUS_GUIA, TRANSICOES_GUIA, labelStatusGuia, corStatusGuia,
    formatarMoeda, formatarData,
} from '../../services/convenios';

export default function Guias() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const soLeitura = isVisualizador();

    const [guias, setGuias] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [convenios, setConvenios] = useState([]);
    const [idPlanoSaude, setIdPlanoSaude] = useState('');
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [status, setStatus] = useState('');
    const [inicio, setInicio] = useState('');
    const [fim, setFim] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [menuAnchor, setMenuAnchor] = useState({ el: null, guia: null });
    const abrirMenu = (e, guia) => setMenuAnchor({ el: e.currentTarget, guia });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));

    const [dialogOperadora, setDialogOperadora] = useState(null);
    const [formOperadora, setFormOperadora] = useState({ numeroGuia: '', senhaAutorizacao: '', observacoes: '' });
    const [dialogStatus, setDialogStatus] = useState(null);
    const [formStatus, setFormStatus] = useState({ status: '', valorPago: '', valorGlosado: '', motivoGlosa: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/planos-saude').then(r => setConvenios(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        /* Pacientes vêm do BuscaPaciente, que consulta o servidor ao digitar. */
    }, []);

    const hasActiveFilters = !!(idPlanoSaude || pacienteSelecionado || status || inicio || fim);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: rowsPerPage };
            if (idPlanoSaude) params.idPlanoSaude = idPlanoSaude;
            if (pacienteSelecionado) params.idPaciente = pacienteSelecionado.id_paciente;
            if (status) params.status = status;
            if (inicio) params.inicio = inicio;
            if (fim) params.fim = fim;
            const res = await api.get('/guias', { params });
            setGuias(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, idPlanoSaude, pacienteSelecionado, status, inicio, fim]);

    useEffect(() => { carregarDados(); }, [carregarDados]);
    useEffect(() => { setPage(0); }, [idPlanoSaude, pacienteSelecionado, status, inicio, fim]);

    const abrirDialogOperadora = () => {
        const guia = menuAnchor.guia;
        fecharMenu();
        setFormOperadora({
            numeroGuia: guia.numeroGuia || '',
            senhaAutorizacao: guia.senhaAutorizacao || '',
            observacoes: guia.observacoes || '',
        });
        setDialogOperadora(guia);
    };

    const salvarDadosOperadora = async () => {
        setSaving(true);
        try {
            await api.put(`/guias/${dialogOperadora.id_guia_atendimento}`, {
                numeroGuia: formOperadora.numeroGuia || null,
                senhaAutorizacao: formOperadora.senhaAutorizacao || null,
                observacoes: formOperadora.observacoes || null,
            });
            toastSuccess('Dados da operadora atualizados');
            setDialogOperadora(null);
            carregarDados();
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const abrirDialogStatus = () => {
        const guia = menuAnchor.guia;
        fecharMenu();
        const possiveis = TRANSICOES_GUIA[guia.status] || [];
        setFormStatus({ status: possiveis[0] || '', valorPago: '', valorGlosado: '', motivoGlosa: '' });
        setDialogStatus(guia);
    };

    const salvarStatus = async () => {
        if (!formStatus.status) { alertValidacao('Escolha a nova situação da guia.'); return; }
        if (formStatus.status === 'GLOSADA' && !formStatus.motivoGlosa.trim()) {
            alertValidacao('Informe o motivo da glosa.');
            return;
        }
        const pagoParcial = formStatus.status === 'PAGA' && formStatus.valorPago !== ''
            && Number(formStatus.valorPago) < Number(dialogStatus.valorTotal);
        if (pagoParcial && !formStatus.motivoGlosa.trim()) {
            alertValidacao('Pagamento menor que o valor da guia exige o motivo da diferença.');
            return;
        }

        setSaving(true);
        try {
            await api.patch(`/guias/${dialogStatus.id_guia_atendimento}/status`, {
                status: formStatus.status,
                valorPago: formStatus.valorPago !== '' ? Number(formStatus.valorPago) : null,
                valorGlosado: formStatus.valorGlosado !== '' ? Number(formStatus.valorGlosado) : null,
                motivoGlosa: formStatus.motivoGlosa || null,
            });
            toastSuccess(`Guia marcada como ${labelStatusGuia(formStatus.status).toLowerCase()}`);
            setDialogStatus(null);
            carregarDados();
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const limparFiltros = () => {
        setIdPlanoSaude('');
        setPacienteSelecionado(null);
        setStatus('');
        setInicio('');
        setFim('');
    };

    const transicoesDisponiveis = dialogStatus ? (TRANSICOES_GUIA[dialogStatus.status] || []) : [];

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Guias de atendimento
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/guias/novo')}>
                        Emitir guia
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {totalElements} guia(s){hasActiveFilters && ' • Filtros aplicados'}
                    </Typography>
                    <Tooltip title="Filtros">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Convênio</InputLabel>
                            <Select value={idPlanoSaude} label="Convênio" onChange={(e) => setIdPlanoSaude(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {convenios.map(c => <MenuItem key={c.id_plano_saude} value={c.id_plano_saude}>{c.nome}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Box sx={{ minWidth: 240 }}>
                            <BuscaPaciente
                                value={pacienteSelecionado}
                                onChange={(v) => setPacienteSelecionado(v)}
                                textFieldProps={{ sx: { bgcolor: '#fff' } }}
                            />
                        </Box>

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Situação</InputLabel>
                            <Select value={status} label="Situação" onChange={(e) => setStatus(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {Object.keys(STATUS_GUIA).map(k => (
                                    <MenuItem key={k} value={k}>{STATUS_GUIA[k].label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField size="small" type="date" label="Atendimento de" InputLabelProps={{ shrink: true }}
                            value={inicio} onChange={(e) => setInicio(e.target.value)} sx={{ bgcolor: '#fff' }} />
                        <TextField size="small" type="date" label="até" InputLabelProps={{ shrink: true }}
                            value={fim} onChange={(e) => setFim(e.target.value)} sx={{ bgcolor: '#fff' }} />

                        {hasActiveFilters && (
                            <Button variant="text" color="error" size="small" onClick={limparFiltros} sx={{ fontWeight: 'bold' }}>
                                Limpar filtros
                            </Button>
                        )}
                    </Box>
                </Collapse>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell><strong>Guia</strong></TableCell>
                                    <TableCell><strong>Paciente</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Convênio</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Atendimento</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Procedimento</strong></TableCell>
                                    <TableCell align="right"><strong>Valor</strong></TableCell>
                                    <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Em aberto</strong></TableCell>
                                    <TableCell><strong>Situação</strong></TableCell>
                                    <TableCell align="right"><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {guias.length > 0 ? guias.map((g) => (
                                    <TableRow key={g.id_guia_atendimento} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DescriptionIcon fontSize="small" color="action" />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {g.numeroGuia || `#${g.id_guia_atendimento}`}
                                                    </Typography>
                                                    {g.idLoteFaturamento && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Lote {g.idLoteFaturamento}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{g.nomePaciente}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{g.nomePlanoSaude}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>{formatarData(g.dataAtendimento)}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            <Tooltip title={g.descricaoProcedimento || ''}>
                                                <span>{g.codigoProcedimento}{g.quantidade > 1 ? ` ×${g.quantidade}` : ''}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{formatarMoeda(g.valorTotal)}</TableCell>
                                        <TableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {formatarMoeda(g.saldoEmAberto)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={STATUS_GUIA[g.status]?.ajuda || ''}>
                                                <Chip size="small" label={labelStatusGuia(g.status)} color={corStatusGuia(g.status)} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => abrirMenu(e, g)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                Nenhuma guia encontrada.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Menu anchorEl={menuAnchor.el} open={Boolean(menuAnchor.el)} onClose={fecharMenu}>
                    {!soLeitura && (
                        <MenuItem onClick={abrirDialogOperadora}
                            disabled={['PAGA', 'CANCELADA'].includes(menuAnchor.guia?.status)}>
                            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText>Número e senha da operadora</ListItemText>
                        </MenuItem>
                    )}
                    {!soLeitura && (
                        <MenuItem onClick={abrirDialogStatus}
                            disabled={!(TRANSICOES_GUIA[menuAnchor.guia?.status] || []).length}>
                            <ListItemIcon><SwapHorizIcon fontSize="small" color="action" /></ListItemIcon>
                            <ListItemText>Alterar situação</ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => { const g = menuAnchor.guia; fecharMenu(); navigate(`/lotes-faturamento/${g?.idLoteFaturamento}`); }}
                        disabled={!menuAnchor.guia?.idLoteFaturamento}>
                        <ListItemIcon><InventoryIcon fontSize="small" color="action" /></ListItemIcon>
                        <ListItemText>Ver lote</ListItemText>
                    </MenuItem>
                </Menu>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>

            {/* Número e senha vêm da operadora depois da emissão; valor e procedimento não mudam aqui. */}
            <Dialog open={Boolean(dialogOperadora)} onClose={() => setDialogOperadora(null)} fullWidth maxWidth="sm">
                <DialogTitle>Dados da operadora</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Alert severity="info">
                            Para corrigir procedimento, valor ou data, cancele a guia e emita outra — o valor é cópia
                            da tabela vigente no dia do atendimento.
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 1, minWidth: 160 }} label="Número da guia"
                                value={formOperadora.numeroGuia}
                                onChange={(e) => setFormOperadora(p => ({ ...p, numeroGuia: e.target.value }))}
                                helperText="Exigido para faturar a guia." />
                            <TextField sx={{ flex: 1, minWidth: 160 }} label="Senha de autorização"
                                value={formOperadora.senhaAutorizacao}
                                onChange={(e) => setFormOperadora(p => ({ ...p, senhaAutorizacao: e.target.value }))} />
                        </Box>
                        <TextField label="Observações" multiline rows={2} inputProps={{ maxLength: 255 }}
                            value={formOperadora.observacoes}
                            onChange={(e) => setFormOperadora(p => ({ ...p, observacoes: e.target.value }))} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOperadora(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={salvarDadosOperadora} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Única porta de troca de situação: as opções são as transições válidas da guia. */}
            <Dialog open={Boolean(dialogStatus)} onClose={() => setDialogStatus(null)} fullWidth maxWidth="sm">
                <DialogTitle>Alterar situação da guia</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Situação atual: <strong>{labelStatusGuia(dialogStatus?.status)}</strong> • Valor da guia:{' '}
                            <strong>{formatarMoeda(dialogStatus?.valorTotal)}</strong>
                        </Typography>

                        <TextField select label="Nova situação *" value={formStatus.status}
                            onChange={(e) => setFormStatus(p => ({ ...p, status: e.target.value }))}>
                            {transicoesDisponiveis.map(s => (
                                <MenuItem key={s} value={s}>{labelStatusGuia(s)}</MenuItem>
                            ))}
                        </TextField>

                        {formStatus.status === 'FATURADA' && (
                            <Alert severity="info">
                                Faturar exige o número da guia dado pela operadora. Reapresentar guia glosada limpa a
                                glosa anterior e devolve o saldo em aberto.
                            </Alert>
                        )}

                        {formStatus.status === 'PAGA' && (
                            <>
                                <TextField type="number" label="Valor pago"
                                    value={formStatus.valorPago}
                                    onChange={(e) => setFormStatus(p => ({ ...p, valorPago: e.target.value }))}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    helperText="Vazio = valor integral da guia." />
                                <TextField label="Motivo da diferença"
                                    value={formStatus.motivoGlosa}
                                    onChange={(e) => setFormStatus(p => ({ ...p, motivoGlosa: e.target.value }))}
                                    helperText="Obrigatório quando o pagamento é menor que o valor da guia: a diferença é lançada como glosa." />
                            </>
                        )}

                        {formStatus.status === 'GLOSADA' && (
                            <>
                                <TextField type="number" label="Valor glosado"
                                    value={formStatus.valorGlosado}
                                    onChange={(e) => setFormStatus(p => ({ ...p, valorGlosado: e.target.value }))}
                                    inputProps={{ min: 0, step: '0.01' }}
                                    helperText="Vazio = glosa integral." />
                                <TextField label="Motivo da glosa *"
                                    value={formStatus.motivoGlosa}
                                    onChange={(e) => setFormStatus(p => ({ ...p, motivoGlosa: e.target.value }))} />
                            </>
                        )}

                        {formStatus.status === 'CANCELADA' && (
                            <Alert severity="warning">
                                Guia já enviada ao convênio não pode ser cancelada, e guia presa a um lote precisa
                                ser removida do lote antes.
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogStatus(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={salvarStatus} disabled={saving}>
                        {saving ? 'Salvando...' : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
